/**
 * MicroAppPanel — Cap'n Web iframe host for Ros micro-apps.
 *
 * Ported from Cloudflare OS `GadgetUI.tsx` (workshop-frontend):
 *   - inject Cap'n Web via capnweb?raw + nested data: URL module (CF OS documented approach)
 *   - MessagePort handshake ("handshake" + port)
 *   - newMessagePortRpcSession(port, forwardingTarget)
 *   - CF OS sandbox CSP (connect-src 'none'; no CDN)
 *   - handshake effect uses refs + [] so parent re-renders do not tear down the session
 *   - resetConnection disposes stub/session on unmount / reload
 *
 * Ros adaptation: forwardingTarget methods POST /api/micro-apps/:id/rpc
 * (parent does fetch; iframe stays connect-src none, matching CF OS).
 *
 * Memory (critical):
 *   Stamped WA clients are ~0.5MB JS → ~0.9MB URL-encoded srcDoc. NEVER reload that on
 *   auth token renewal (AppShell already keeps WebChatService stable for the same reason).
 *   Do not keep the giant HTML string in React state after assigning to the iframe.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import { newMessagePortRpcSession, RpcTarget } from 'capnweb'
import CAPNWEB_BUNDLE from 'capnweb?raw'
import {
  buildMicroAppRpcBody,
  microAppAssetApiPath,
  microAppRuntimeErrorMessage,
  readMicroAppAssetResponse,
} from './microAppBridge'
import { createMicroAppSandboxedHtml } from './microAppSandbox'

// CF OS: annotate for DevTools; embed whole Cap'n Web (no deps) into the gadget.
const CAPNWEB_BUNDLE_ANNOTATED = `//# sourceURL=jsrpc.js\n${CAPNWEB_BUNDLE}`

// CF OS INJECTED_CODE_PREFIX: outer URL-encoded module imports Cap'n Web from inner base64 data: URL.
const INJECTED_CODE_PREFIX = encodeURIComponent(String.raw`//# sourceURL=client-prefix.js
import { RpcTarget, RpcStub, newMessagePortRpcSession } from "data:text/javascript;charset=utf-8;base64,${btoa(CAPNWEB_BUNDLE_ANNOTATED)}";

let gadget;
{
  let {port1, port2} = new MessageChannel();
  window.parent.postMessage("handshake", "*", [port2]);
  gadget = newMessagePortRpcSession(port1);
}

const ros = Object.freeze({
  call(action, input = null) {
    return gadget.call(action, input);
  },
  async assetUrl(path) {
    const asset = await gadget.asset(path);
    return URL.createObjectURL(new Blob([asset.bytes], { type: asset.contentType }));
  },
  revokeAssetUrl(url) {
    URL.revokeObjectURL(url);
  },
});
Object.defineProperty(globalThis, "ros", {
  value: ros,
  writable: false,
  configurable: false,
});

const blockedOpen = () => {
  console.error("window.open() is disabled in micro-app UIs.");
  return null;
};
window.open = blockedOpen;
globalThis.open = blockedOpen;
try { Window.prototype.open = blockedOpen; } catch {}

window.addEventListener('error', (event) => {
  window.parent.postMessage({
    type: 'console',
    level: 'error',
    message: ['Uncaught', event.error?.stack || event.message],
  }, '*');
});
window.addEventListener('unhandledrejection', (event) => {
  let reason = event.reason;
  window.parent.postMessage({
    type: 'console',
    level: 'error',
    message: ['Unhandled promise rejection:', reason?.stack || String(reason)],
  }, '*');
});

`)

function nukeIframeDocument(iframe: HTMLIFrameElement | null) {
  if (!iframe) return
  try {
    iframe.srcdoc = ''
    iframe.removeAttribute('srcdoc')
    iframe.src = 'about:blank'
  } catch {
    // ignore
  }
}

export interface MicroAppPanelProps {
  definitionId: string
  /** Pins an App Artifact preview to one immutable uploaded package version. */
  artifactVersion?: number
  apiBaseUrl: string
  token: string | null
  /** When false, drop srcDoc so the iframe heap can be collected (CF OS visibility pattern). */
  isVisible?: boolean
}

export function MicroAppPanel({
  definitionId,
  artifactVersion,
  apiBaseUrl,
  token,
  isVisible = true,
}: MicroAppPanelProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  /** Pending srcDoc assigned once in layout effect, then cleared (do not retain in React state). */
  const pendingSrcDocRef = useRef<string | null>(null)
  const [frameReady, setFrameReady] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [iframeGeneration, setIframeGeneration] = useState(0)

  const loadGenerationRef = useRef(0)
  const connectionGenerationRef = useRef(0)
  const handshakePendingRef = useRef<number | null>(null)
  const rpcSessionRef = useRef<{ [Symbol.dispose]?: () => void } | null>(null)

  // Keep bridge config in refs so the handshake effect can stay [] (CF OS pattern).
  // Token MUST stay in a ref only: renewal must not reload the stamped Cap'n Web document.
  const definitionIdRef = useRef(definitionId)
  const artifactVersionRef = useRef(artifactVersion)
  const apiBaseUrlRef = useRef(apiBaseUrl)
  const tokenRef = useRef(token)
  definitionIdRef.current = definitionId
  artifactVersionRef.current = artifactVersion
  apiBaseUrlRef.current = apiBaseUrl
  tokenRef.current = token

  const resetConnection = (reason: unknown) => {
    ++connectionGenerationRef.current
    handshakePendingRef.current = null
    try {
      rpcSessionRef.current?.[Symbol.dispose]?.()
    } catch {
      // ignore dispose errors
    }
    rpcSessionRef.current = null
    void reason
  }

  // Load stamped client.js → pending srcDoc. Intentionally NOT dependent on `token`
  // (see AppShell WebChatService: token renewal must not recreate heavy sessions).
  useEffect(() => {
    if (!isVisible) {
      nukeIframeDocument(iframeRef.current)
      pendingSrcDocRef.current = null
      setFrameReady(false)
      setLoading(false)
      resetConnection(new Error('Micro-app panel hidden.'))
      return
    }

    const generation = ++loadGenerationRef.current
    const isCurrent = () => loadGenerationRef.current === generation

    // Drop the previous iframe document before fetching a replacement.
    nukeIframeDocument(iframeRef.current)
    pendingSrcDocRef.current = null
    setFrameReady(false)
    setLoading(true)
    setError(null)

    ;(async () => {
      try {
        const headers: Record<string, string> = {}
        const tok = tokenRef.current
        if (tok) headers.Authorization = `Bearer ${tok}`
        const res = await fetch(
          `${apiBaseUrl}/api/micro-apps/${encodeURIComponent(definitionId)}/client.js${artifactVersion ? `?artifactVersion=${artifactVersion}` : ''}`,
          { headers, credentials: 'include' },
        )
        if (!res.ok) throw new Error(`Failed to load client.js (${res.status})`)
        const clientJs = await res.text()
        if (!isCurrent()) return
        pendingSrcDocRef.current = createMicroAppSandboxedHtml(INJECTED_CODE_PREFIX, clientJs)
        setIframeGeneration((g) => g + 1)
        setFrameReady(true)
      } catch (err) {
        if (!isCurrent()) return
        pendingSrcDocRef.current = null
        setFrameReady(false)
        setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (isCurrent()) setLoading(false)
      }
    })()

    return () => {
      if (isCurrent()) loadGenerationRef.current++
      nukeIframeDocument(iframeRef.current)
      pendingSrcDocRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- token via tokenRef only
  }, [definitionId, artifactVersion, apiBaseUrl, isVisible])

  // Assign srcdoc once the iframe node exists, then drop our retained string copy.
  useLayoutEffect(() => {
    if (!frameReady) return
    const el = iframeRef.current
    const html = pendingSrcDocRef.current
    if (!el || !html) return
    el.srcdoc = html
    pendingSrcDocRef.current = null
  }, [frameReady, iframeGeneration, definitionId])

  // CF OS handshake effect: mount once; dispose only on unmount. Do not depend on html/token.
  useEffect(() => {
    let cancelled = false

    const handleMessage = (event: MessageEvent) => {
      if (
        event.source !== iframeRef.current?.contentWindow ||
        event.origin !== 'null'
      ) {
        return
      }

      const runtimeError = microAppRuntimeErrorMessage(event.data)
      if (runtimeError) {
        setError(runtimeError)
        return
      }

      if (event.data === 'handshake' && event.ports?.[0]) {
        const port = event.ports[0]
        resetConnection(new Error('Micro-app iframe reloaded.'))
        const generation = connectionGenerationRef.current
        handshakePendingRef.current = generation
        const isCurrent = () =>
          !cancelled &&
          generation === connectionGenerationRef.current &&
          event.source === iframeRef.current?.contentWindow

        try {
          const forwardingTarget = new Proxy(new RpcTarget() as object, {
            get(target, property, receiver) {
              if (typeof property === 'symbol' || property in (target as object)) {
                return Reflect.get(target, property, receiver)
              }
              return async (...args: unknown[]) => {
                const headers: Record<string, string> = {
                  'Content-Type': 'application/json',
                }
                const tok = tokenRef.current
                if (tok) headers.Authorization = `Bearer ${tok}`
                if (property === 'asset') {
                  const assetPath = microAppAssetApiPath(
                    definitionIdRef.current,
                    args[0],
                    artifactVersionRef.current,
                  )
                  const assetRes = await fetch(`${apiBaseUrlRef.current}${assetPath}`, {
                    credentials: 'include',
                    headers,
                  })
                  if (!assetRes.ok) {
                    throw new Error(`Asset failed (${assetRes.status})`)
                  }
                  return readMicroAppAssetResponse(assetRes)
                }
                const res = await fetch(
                  `${apiBaseUrlRef.current}/api/micro-apps/${encodeURIComponent(definitionIdRef.current)}/rpc${artifactVersionRef.current ? `?artifactVersion=${artifactVersionRef.current}` : ''}`,
                  {
                    method: 'POST',
                    credentials: 'include',
                    headers,
                    body: JSON.stringify(buildMicroAppRpcBody(property, args)),
                  },
                )
                const data = (await res.json()) as {
                  ok?: boolean
                  result?: unknown
                  error?: string
                }
                if (!res.ok || !data.ok) {
                  throw new Error(data.error || `RPC ${String(property)} failed`)
                }
                return data.result
              }
            },
          })

          if (!isCurrent()) {
            port.close()
            return
          }
          rpcSessionRef.current = newMessagePortRpcSession(
            port,
            forwardingTarget as never,
          )
        } catch (err) {
          port.close()
          if (!isCurrent()) return
          setError(err instanceof Error ? err.message : String(err))
        } finally {
          if (handshakePendingRef.current === generation) {
            handshakePendingRef.current = null
          }
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => {
      cancelled = true
      window.removeEventListener('message', handleMessage)
      resetConnection(new Error('Micro-app RPC session was closed.'))
      nukeIframeDocument(iframeRef.current)
      pendingSrcDocRef.current = null
    }
  }, [])

  if (!isVisible) {
    return null
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 3 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading app…</Typography>
      </Box>
    )
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    )
  }
  if (!frameReady) return null

  return (
    <Box sx={{ height: '100%', minHeight: 0, overflow: 'hidden', bgcolor: 'background.paper' }}>
      <iframe
        key={`${definitionId}:${artifactVersion ?? 'latest'}:${iframeGeneration}`}
        ref={iframeRef}
        title={`app-artifact-${definitionId}`}
        sandbox="allow-scripts"
        style={{ display: 'block', width: '100%', height: '100%', border: 0 }}
      />
    </Box>
  )
}
